/**
 * @module routes.Sessions.AllCoachesPublicSession._components.coachMeta
 *
 * Pure derivations behind the "Book a Session / By Coach" screen — the coach
 * card's chip and meta line, the facility summary line, and search matching.
 * See .figma-design/CN-2652/spec.md (frame 526:28677).
 */
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import {
  PackageCredit,
  getApplicablePackageCredit,
  rateRequiresPackage,
} from "app/utils/creditBundles";

/**
 * The rates an athlete can actually book. The backend computes
 * availablePaymentMethods from Stripe status, credit bundles and collectType,
 * and leaves it empty for rates nobody can pay for — those are hidden
 * everywhere else in the booking flow, so they must not be counted here
 * either or the chip promises session types the coach page won't show.
 */
export const bookableRates = (coach: CoachProfileModel): LessonRateModel[] =>
  (coach?.lessonRates || []).filter(
    (rate) => rate.availablePaymentMethods && rate.availablePaymentMethods.length > 0,
  );

export const sessionTypeCount = (coach: CoachProfileModel): number =>
  bookableRates(coach).length;

/**
 * The coach's title, e.g. "PGA Master" — set per facility in Admin Settings → Users
 * and carried through to the booking payload. Blank for a coach nobody has given one,
 * and the card drops the line entirely rather than leaving a gap.
 *
 * The frame draws "PGA Master • 8 yrs exp". Years of experience has no field anywhere
 * yet and is deliberately out of scope, so only the title half is built; when it does
 * arrive it joins here with a " • ".
 */
export const coachMetaLine = (coach: CoachProfileModel): string =>
  (coach?.title || "").trim();

/**
 * The letters that stand in for a coach with no photo: first and last initial,
 * "DP" for Devon Park. One name gives one letter, and a coach with no name at all
 * gives none — the caller draws the empty box it always did.
 *
 * `Array.from` rather than `[0]`, so a name that starts outside the basic plane
 * contributes its whole character instead of half a surrogate pair.
 */
export const coachInitials = (coach: CoachProfileModel): string => {
  const name = (
    coach?.fullName ||
    [coach?.firstName, coach?.lastName].filter(Boolean).join(" ")
  )
    .toString()
    .trim();

  if (name === "") {
    return "";
  }

  const parts = name.split(/\s+/);
  const first = Array.from(parts[0])[0] || "";
  const last =
    parts.length > 1 ? Array.from(parts[parts.length - 1])[0] || "" : "";
  return (first + last).toUpperCase();
};

/** Summary under the facility name: "6 coaches · 24 session types". */
export const facilitySummary = (coaches: CoachProfileModel[]) => ({
  coachCount: coaches.length,
  sessionTypeCount: coaches.reduce((total, coach) => total + sessionTypeCount(coach), 0),
});

/**
 * Search matches name and title — the placeholder promises both. Blank and
 * whitespace-only queries match everything so clearing the box restores the
 * full list.
 */
export const matchesCoachSearch = (
  coach: CoachProfileModel,
  query: string,
): boolean => {
  const needle = (query || "").trim().toLowerCase();
  if (needle === "") {
    return true;
  }
  const haystacks = [coach?.fullName, coach?.title];
  return haystacks.some((value) => (value || "").toLowerCase().includes(needle));
};

/**
 * The order booking options are offered in, and so which one comes first:
 *
 *   1. covered by a package the athlete holds — unlimited first, then the most
 *      sessions remaining
 *   2. the rest, in the coach's configured order
 *   3. package-only options the athlete cannot use yet, last
 *
 * Comment #31 asks the coach calendar to open with the top option of exactly
 * this order pre-selected ("package/subscription, then coach designated
 * order"), which is also the order `AllCoachesPanel` lists them in — hence one
 * shared function instead of two.
 */
export const orderRatesForAthlete = (
  rates: LessonRateModel[],
  packageCredits?: Map<number, PackageCredit>,
  packageFeatureEnabled = true,
): LessonRateModel[] => {
  const creditFor = (rate: LessonRateModel) =>
    getApplicablePackageCredit(rate, packageCredits);
  const needsPackage = (rate: LessonRateModel) =>
    Boolean(packageFeatureEnabled) && rateRequiresPackage(rate, packageCredits);

  /**
   * The coach's own order, before anything is grouped by it — step 2 above,
   * which this function described but never did. It took the payload's order on
   * trust, and `/api/public/coach_profiles/:calendar_uuid`, which is what the
   * public coach calendar is built from, does not order its `lesson_rates` at
   * all: the same coach comes back 5, 2, 8, 1, 6, 4, 7, 3. Every other screen
   * looked right only because the endpoint behind it happened to sort — the
   * facility payload and `landing_page_coach` both do, as does
   * `parsePricingValues` for the option dropdown on the calendar itself.
   *
   * An unordered option sorts last rather than first, so a coach who has never
   * touched the order keeps the order they had.
   */
  const inCoachOrder = [...rates].sort(
    (a, b) =>
      (a.orderNumber ?? Number.MAX_SAFE_INTEGER) -
      (b.orderNumber ?? Number.MAX_SAFE_INTEGER),
  );

  const covered = inCoachOrder
    .filter((rate) => creditFor(rate))
    .sort((a, b) => {
      const creditA = creditFor(a)!;
      const creditB = creditFor(b)!;
      if (creditA.unlimited !== creditB.unlimited) {
        return creditA.unlimited ? -1 : 1;
      }
      return creditB.remainingSessions - creditA.remainingSessions;
    });
  const unusable = inCoachOrder.filter((rate) => needsPackage(rate));
  const rest = inCoachOrder.filter(
    (rate) => !creditFor(rate) && !needsPackage(rate),
  );

  return [...covered, ...rest, ...unusable];
};

/** The option a coach's calendar should open on — see `orderRatesForAthlete`. */
export const topRateForAthlete = (
  coach: CoachProfileModel,
  packageCredits?: Map<number, PackageCredit>,
  packageFeatureEnabled = true,
): LessonRateModel | null =>
  orderRatesForAthlete(
    bookableRates(coach),
    packageCredits,
    packageFeatureEnabled,
  )[0] ?? null;

/**
 * Drives the "You can apply your Package" ribbon: true when at least one of the
 * coach's bookable rates is covered by a package the athlete already holds.
 * Anonymous visitors have no credits, so this is always false for them.
 */
export const coachAcceptsPackage = (
  coach: CoachProfileModel,
  packageCredits?: Map<number, PackageCredit>,
): boolean =>
  bookableRates(coach).some((rate) =>
    Boolean(getApplicablePackageCredit(rate, packageCredits)),
  );

/**
 * Coaches a package can be applied with come first — the Figma comment on frame
 * 548:74607, where the one card carrying the ribbon is drawn last and so falls
 * below the fold at a facility with a full roster.
 *
 * Stable in both halves: the facility's own order decides within each, so the
 * list an anonymous visitor sees is exactly the payload order and a logged-in
 * athlete sees the same list with their packaged coaches lifted out of it.
 */
export const orderCoachesForAthlete = (
  coaches: CoachProfileModel[],
  packageCredits?: Map<number, PackageCredit>,
): CoachProfileModel[] => {
  const withPackage = coaches.filter((coach) =>
    coachAcceptsPackage(coach, packageCredits),
  );
  if (withPackage.length === 0 || withPackage.length === coaches.length) {
    return coaches;
  }
  return [
    ...withPackage,
    ...coaches.filter((coach) => !coachAcceptsPackage(coach, packageCredits)),
  ];
};
